import React from 'react';
import { formatSupabaseUiError } from '../utils/supabaseErrors';
import { readableErrorMessage } from '../utils/appErrors';
import { ACTION_FAILURE_EVENT, isOwnedReadCancellation } from '../lib/actionFailureSignals';

type ToastKind = 'success'|'error'|'info'|'warn';
const DEFAULT_TOAST_TTL_MS = 4000;
const DEFAULT_ERROR_TOAST_TTL_MS = 10000;
const UNHANDLED_ERROR_DEDUPE_MS = 1500;

export interface Toast {
  id: string;
  type?: ToastKind;
  severity?: ToastKind;
  message: string;
  ttl?: number;
  /** Original failure, when available, for a precise user-facing explanation. */
  error?: unknown;
}
type ToastInput = Omit<Toast, 'id'>;
interface ToastContextValue { push(t: ToastInput): string; remove(id: string): void; }

const ToastCtx = React.createContext<ToastContextValue | null>(null);
export function useToasts(){
  const ctx = React.useContext(ToastCtx);
  if(!ctx) throw new Error('useToasts must be used within <ToastProvider>');
  return ctx;
}

export default function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const lastUnhandledErrorRef = React.useRef<{ message: string; at: number } | null>(null);
  const lastErrorToastRef = React.useRef<{ id: string; message: string; at: number } | null>(null);
  const pendingFailureRef = React.useRef<{ message: string; at: number } | null>(null);
  const push = React.useCallback((t: ToastInput)=>{
    const id = crypto.randomUUID();
    const type = t.type ?? t.severity ?? 'info';
    const ttl = t.ttl ?? (type === 'error' ? DEFAULT_ERROR_TOAST_TTL_MS : DEFAULT_TOAST_TTL_MS);
    let message = type === 'error' ? readableErrorMessage(t.error ?? t.message) : t.message;
    const pending = pendingFailureRef.current;
    if (type === 'error' && !t.error && pending && Date.now() - pending.at < 200
      && (/^(?:failed|unable|could not|couldn't) to (?:save|update|create)/i.test(t.message)
        || message.startsWith('The app could not complete this action.'))) message = pending.message;
    if (type === 'error') {
      const previous = lastErrorToastRef.current;
      if (previous?.message === message && Date.now() - previous.at < UNHANDLED_ERROR_DEDUPE_MS) return previous.id;
      lastErrorToastRef.current = { id, message, at: Date.now() };
    }
    setToasts(prev=>[...prev,{...t,message,type,id}]);
    if(ttl>0){
      setTimeout(()=>{
        setToasts(prev=>prev.filter(x=>x.id!==id));
        if (lastErrorToastRef.current?.id === id) lastErrorToastRef.current = null;
      }, ttl);
    }
    return id;
  }, []);
  const remove = React.useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    if (lastErrorToastRef.current?.id === id) lastErrorToastRef.current = null;
  }, []);
  const contextValue = React.useMemo(() => ({ push, remove }), [push, remove]);

  React.useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const handleFailure = (event: Event) => {
      const message = (event as CustomEvent<{ message: string }>).detail.message;
      const reportedAt = Date.now();
      pendingFailureRef.current = { message, at: reportedAt };
      // Give the owning form time to present its more specific feedback first. Any error toast shown
      // since the report is that feedback, in the form's own words or with this message (see push).
      const timer = setTimeout(() => {
        timers.delete(timer);
        if (lastErrorToastRef.current && lastErrorToastRef.current.at >= reportedAt) return;
        push({ type: 'error', message });
      }, 200);
      timers.add(timer);
    };
    window.addEventListener(ACTION_FAILURE_EVENT, handleFailure);
    return () => {
      window.removeEventListener(ACTION_FAILURE_EVENT, handleFailure);
      timers.forEach(clearTimeout);
    };
  }, [push]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const publishUnhandledError = (reason: unknown) => {
      if (isOwnedReadCancellation(reason)) return;

      const message = formatSupabaseUiError(
        reason,
        'Something went wrong while completing that action.',
      );
      const now = Date.now();
      const previous = lastUnhandledErrorRef.current;
      if (previous && previous.message === message && now - previous.at < UNHANDLED_ERROR_DEDUPE_MS) {
        return;
      }
      lastUnhandledErrorRef.current = { message, at: now };
      push({ type: 'error', message });
    };
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      publishUnhandledError(event.reason);
    };
    const handleWindowError = (event: ErrorEvent) => {
      if (!event.error && !event.message) return;
      publishUnhandledError(event.error || new Error(event.message));
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleWindowError);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleWindowError);
    };
  }, [push]);

  return (
    <ToastCtx.Provider value={contextValue}>
      {children}
      <div className="toast-region" role="region" aria-label="Notifications" title="Notifications appear here">
        {toasts.map(t=> (
          <div
            key={t.id}
            className={`toast toast-${t.type||'info'}`}
            role={t.type === 'error' ? 'alert' : 'status'}
            aria-live={t.type === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
          >
            <span className="msg">{t.message}</span>
            <button className="close" onClick={()=>remove(t.id)} aria-label="Dismiss">×</button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
